import type { GridState, StateUpdatedEvent } from 'ag-grid-community';
import { isEqual } from 'lodash-es';
import { useState, type ReactNode } from 'react';
import { useMixpanel } from '../../contexts/MixpanelContext';
import { useCallbackRef } from '../../hooks';
import { MixpanelEvent } from '../../tokens/mixpanel';
import { logger } from '../../utils';
import { Tab, TabAppearance, TabList, Tabs, type TabSize } from '../Tabs';
import type { UseBlotterTableUtilitiesOutput } from './types';

export const CUSTOM_BLOTTER_GROUPING = 'Custom';

export interface BlotterGroupingDefinition<AllowedGroupings extends string, GroupableColumns extends string> {
  key: AllowedGroupings;
  label: string;
  rowGroupColumns: GroupableColumns[];
  mixpanelEvent?: MixpanelEvent;
  buttonDataTestId?: string;
}

interface UseBlotterGroupingParams<AllowedGroupings extends string, GroupableColumns extends string> {
  /** Just a name for the blotter, used to identify it in a potential warning message. Can be anything. */
  blotterName: string;
  /** The definitions of the groupings you want to support. */
  groupings: BlotterGroupingDefinition<AllowedGroupings, GroupableColumns>[];
  /** The function this hook uses to update blotter grouping state when columns change. Taken from the return object of useBlotterTable. */
  setRowGroupColumns: UseBlotterTableUtilitiesOutput<any>['setRowGroupColumns'];
  /** Allow unselecting all groupings, effectively removing any grouping in the blotter. Default: false. */
  allowNoGrouping?: boolean;

  /** Size of the (tab) buttons returned */
  size?: TabSize;
}

interface UseBlotterGroupingOutput {
  /** Function to connect to ag-grid's stateUpdated event handling. */
  onStateUpdated: (event: StateUpdatedEvent) => void;
  groupingToggleButtons: ReactNode;
}

/** Handle Non-tree-based Row Group Toggling */
export const useBlotterGroupingToggling = <AllowedGroupings extends string, GroupableColumns extends string>({
  blotterName,
  groupings,
  setRowGroupColumns,
  allowNoGrouping,
  size,
}: UseBlotterGroupingParams<AllowedGroupings, GroupableColumns>): UseBlotterGroupingOutput => {
  const mixpanel = useMixpanel();

  const [selectedGrouping, setSelectedGrouping] = useState<AllowedGroupings | undefined>();

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    setSelectedGrouping(stateToGrouping(event.state, groupings, blotterName));
  });

  const handleToggleButtonClicked = useCallbackRef(
    (grouping: BlotterGroupingDefinition<AllowedGroupings, GroupableColumns>) => {
      if (selectedGrouping === grouping.key && allowNoGrouping) {
        // If we're clicking the already selected grouping, and we allow "un-grouping", we remove all grouping
        setRowGroupColumns([]);
        mixpanel.track(MixpanelEvent.GroupingRemoved);
      } else {
        if (grouping.mixpanelEvent) {
          mixpanel.track(grouping.mixpanelEvent);
        }

        setRowGroupColumns(grouping.rowGroupColumns);
      }
    }
  );

  return {
    onStateUpdated: handleGridStateUpdated,
    groupingToggleButtons: (
      <Tabs
        appearance={TabAppearance.Pill}
        selectedIndex={groupings.findIndex(group => group.key === selectedGrouping)}
        w="auto"
        size={size}
      >
        <TabList suppressOverflowList={true}>
          {groupings.map(grouping => (
            <Tab
              key={grouping.key}
              onClick={() => handleToggleButtonClicked(grouping)}
              data-testid={grouping.buttonDataTestId}
              label={grouping.label}
            />
          ))}
        </TabList>
      </Tabs>
    ),
  };
};

export function stateToGrouping<AllowedGroupings extends string, GroupableColumns extends string>(
  state: GridState,
  groupings: UseBlotterGroupingParams<AllowedGroupings, GroupableColumns>['groupings'],
  blotterName: string
): AllowedGroupings | undefined {
  const groupingColIds = state.rowGroup?.groupColIds ?? [];
  const groupingBeingUsed = groupings.find(grouping => isEqual(groupingColIds, grouping.rowGroupColumns))?.key;

  if (groupingBeingUsed == null) {
    const maybeCustomGrouping = groupings.find(grouping => grouping.key === CUSTOM_BLOTTER_GROUPING);
    if (maybeCustomGrouping) {
      // We couldn't find any matching grouping spec, but we do support fully custom groupings, so we return that we are doing Custom grouping.
      return maybeCustomGrouping.key;
    }

    // We weren't able to find anything... shouldn't happen, something is wrong
    logger.warn(`Unable to resolve grouped colIds in blotter: ${blotterName}`, {
      extra: {
        colIds: groupingColIds,
      },
    });
  }

  return groupingBeingUsed;
}
